<template>
  <div
    class="topic-card"
    :style="{
      '--primary-color': topic.color.primary,
      '--secondary-color': topic.color.secondary,
    }"
    @click="navigateToTopic"
  >
    <!-- 顶部彩色条 -->
    <div class="color-strip"></div>

    <!-- 卡片内容 -->
    <div class="card-content">
      <!-- 图标 -->
      <div class="icon-container">
        <span class="icon">{{ topic.icon }}</span>
      </div>

      <!-- 标题 -->
      <h3 class="title">{{ topic.name }}</h3>

      <!-- 描述 -->
      <p class="description">{{ topic.description }}</p>

      <!-- 底部信息 -->
      <div class="footer">
        <div class="article-count">
          <svg
            class="count-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span>{{ articleCount }} 篇</span>
        </div>

        <div class="arrow">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useBlogPosts } from "@/composables/useBlogPosts";

export default {
  name: "TopicFlipCard",
  props: {
    topic: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const { getTopicStats } = useBlogPosts();

    const articleCount = computed(() => {
      return getTopicStats.value[props.topic.id] || 0;
    });

    const navigateToTopic = () => {
      router.push(`/topic/${props.topic.id}`);
    };

    return {
      articleCount,
      navigateToTopic,
    };
  },
};
</script>

<style scoped>
.topic-card {
  width: 240px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
}

.topic-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08);
}

/* 顶部彩色条 */
.color-strip {
  height: 5px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  transition: height 0.3s ease;
}

.topic-card:hover .color-strip {
  height: 8px;
}

/* 卡片内容 */
.card-content {
  padding: 1.75rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* 图标容器 */
.icon-container {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  transition: transform 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.topic-card:hover .icon-container {
  transform: scale(1.08) rotate(-3deg);
}

.icon {
  font-size: 2.25rem;
  line-height: 1;
}

/* 标题 */
.title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.625rem;
  transition: color 0.3s ease;
}

.topic-card:hover .title {
  color: var(--primary-color);
}

/* 描述 */
.description {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #6b7280;
  margin: 0 0 1.25rem;
  min-height: 42px;
}

/* 底部信息 */
.footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.article-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.count-icon {
  width: 16px;
  height: 16px;
  color: var(--primary-color);
}

.arrow {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f9fafb;
  color: var(--primary-color);
  transition: all 0.3s ease;
}

.arrow svg {
  width: 14px;
  height: 14px;
}

.topic-card:hover .arrow {
  background: var(--primary-color);
  color: white;
  transform: translateX(3px);
}

/* 响应式 */
@media (min-width: 640px) {
  .topic-card {
    width: 260px;
  }
}

@media (min-width: 1024px) {
  .topic-card {
    width: 280px;
  }

  .card-content {
    padding: 2rem 1.75rem 1.75rem;
  }
}

@media (max-width: 480px) {
  .topic-card {
    width: 100%;
    max-width: 320px;
  }

  .card-content {
    padding: 1.5rem 1.25rem 1.25rem;
  }

  .icon-container {
    width: 64px;
    height: 64px;
  }

  .icon {
    font-size: 2rem;
  }

  .title {
    font-size: 1.25rem;
  }
}
</style>
